import type { MeasureData } from '../interfaces';
import { BODY_FAT_RANGE_PERCENT } from './calculate-muscle';
import type { CompositionPoint } from './composition';

export interface BodyFatReading {
    scanID: string;
    date: Date;
    percent: number;
}

export interface BodyFatReadings {
    /** Oldest first. */
    readings: BodyFatReading[];
    start: BodyFatReading | null;
    now: BodyFatReading | null;
}

/**
 * Body fat per scan, straight off the measurements — no weight required.
 *
 * The percentage is computed from the tape in loadModels (US Navy formula) and
 * needs nothing else. The redesign read it instead from the composition
 * series, which also needs a weight to split fat from lean in kilograms, and
 * real scans often carry none. So a client with unweighed scans read "Now
 * 0 %", and one whose latest scan was unweighed read the scan before it.
 * Anything that is a percentage reads from here; only kilograms need
 * composition.
 */
export const bodyFatReadings = (measurements: MeasureData[]): BodyFatReadings => {
    const readings = measurements
        .map((m) => ({
            scanID: m.ID,
            date: new Date(m.createdAt),
            percent: m.measurements?.body_fat as number,
        }))
        .filter(
            (r) =>
                Number.isFinite(r.percent) &&
                r.percent >= BODY_FAT_RANGE_PERCENT.min &&
                r.percent <= BODY_FAT_RANGE_PERCENT.max,
        )
        .sort((a, b) => a.date.getTime() - b.date.getTime());

    return {
        readings,
        start: readings[0] ?? null,
        now: readings[readings.length - 1] ?? null,
    };
};

/**
 * Progress toward a body-fat target, in points of body fat.
 *
 * Needs only percentages, so it does not wait on a weighed scan either. Null
 * without a target, or when the target is not below where the client started
 * — there is then no distance to cover.
 */
export const goalProgressFromBodyFat = (
    startPercent: number,
    nowPercent: number,
    targetPercent: number | undefined,
): { fraction: number; pointsDone: number; pointsTotal: number } | null => {
    if (targetPercent === undefined) return null;
    const pointsTotal = startPercent - targetPercent;
    if (pointsTotal <= 0) return null;
    const pointsDone = startPercent - nowPercent;
    return {
        fraction: Math.min(1, Math.max(0, pointsDone / pointsTotal)),
        pointsDone,
        pointsTotal,
    };
};

/** One scan as a Then or Now column shows it. */
export interface ColumnReading {
    date: Date;
    bodyFatPercent: number;
    /** From this scan, or from `weighedOn` when carried. */
    weightKg?: number;
    /** From this scan, or from `weighedOn` when carried. */
    leanKg?: number;
    /**
     * Set when this scan was not weighed and weight and lean were carried
     * from an earlier one: the date of that scan, which the column prints
     * beside them.
     */
    weighedOn?: Date;
}

/**
 * A scan's body fat, with its weight and lean mass.
 *
 * Matched to the composition point by date, since both come from the same
 * createdAt. With `carry`, an unweighed scan takes weight and lean from the
 * last weighed scan before it — and says so with that scan's date. Without
 * the date, a Now column pairing the latest body fat with an older weight
 * would state two dates as one; without the carry, the Now column read "—"
 * while the Measurements card a tab away read the same scan's 90 kg.
 */
export const columnReading = (
    reading: BodyFatReading,
    points: CompositionPoint[],
    { carry = false }: { carry?: boolean } = {},
): ColumnReading => {
    const kgOf = (p: CompositionPoint) => ({
        weightKg: p.fatKg + p.leanKg,
        leanKg: p.leanKg,
    });
    const p = points.find((x) => x.date.getTime() === reading.date.getTime());
    if (p) return { date: reading.date, bodyFatPercent: reading.percent, ...kgOf(p) };
    const earlier = carry
        ? [...points].reverse().find((x) => x.date.getTime() < reading.date.getTime())
        : undefined;
    return {
        date: reading.date,
        bodyFatPercent: reading.percent,
        ...(earlier ? { ...kgOf(earlier), weighedOn: earlier.date } : {}),
    };
};
